import type { Department } from "../contexts/AuthProvider";
import type { TrainingWageType } from "../pages/Settings/DepartmentConfiguration/forms/types";
import {
	DEFAULT_HOLIDAY_RATE_PAY_PERCENTAGE,
	DEFAULT_NIGHT_PAY_VALUE,
	DEFAULT_ORIENTATION_WAGE_AMOUNT,
	DEFAULT_ORIENTATION_WAGE_TYPE,
	DEFAULT_OVERTIME_DAILY_HOURS_LIMIT,
	DEFAULT_OVERTIME_RATE_PAY_PERCENTAGE,
	DEFAULT_OVERTIME_WEEKLY_HOURS_LIMIT,
	DEFAULT_TRAINING_WAGE_AMOUNT,
	DEFAULT_TRAINING_WAGE_TYPE,
	DEFAULT_WEEKEND_PAY_VALUE,
} from "../pages/Settings/DepartmentConfiguration/utils";
import type { NurseRole } from "./models";

export interface DepartmentWageConfig {
	trainingWageType: TrainingWageType;
	trainingWageValue: number;
	orientationWageType: TrainingWageType;
	orientationWageValue: number;
	overtimeRatePayPercentage: number;
	nightRatePayValue: Partial<Record<NurseRole, number>>;
	weekendRatePayValue: Partial<Record<NurseRole, number>>;
	holidayRatePayPercentage: number;
	overtimeDailyHoursLimit: number;
	overtimeWeeklyHoursLimit: number;
}
/**
 * Largest storable flat amount: the wage columns are `DECIMAL(10, 2)`, so 99999999.99. Mirrors the
 * backend's `FLAT_RATE_WAGE_MAX`, which the training/orientation pair and the DAT-905 rule tables'
 * bonus and rate fields are all bounded by.
 */
export const FLAT_RATE_WAGE_MAX = 99999999.99;

/** Body of `PUT /departments/{id}/training-orientation-wage`. All four are required: the rates are a pair. */
export type TrainingOrientationWageBody = Pick<DepartmentWageConfig, "orientationWageType" | "orientationWageValue" | "trainingWageType" | "trainingWageValue">;

/** The wage columns are `DECIMAL(10, 2)`, so the server stores two places whatever it is sent. */
const WAGE_VALUE_DECIMALS = 2;

/**
 * Mirrors the backend's `Decimal(str(value)).quantize(Decimal("0.01"), ROUND_HALF_UP)`. `toFixed(2)`
 * cannot: it rounds the binary double, so `2.675` (held as `2.674999…`) gives `2.67`, not `2.68`.
 */
const quantizeWageValue = (value: number): number => {
	const text = String(value);
	// JS switches to exponent notation below 1e-6, and a sub-cent wage does reach here. Anything that
	// small quantizes to zero, which is what the column stores.
	if (text.includes("e") || text.includes("E")) return 0;
	if (!text.includes(".")) return value;
	const [whole, fraction] = text.split(".");
	if (fraction.length <= WAGE_VALUE_DECIMALS) return value;
	const truncatedCents = Math.round(Number(`${whole}.${fraction.slice(0, WAGE_VALUE_DECIMALS)}`) * 100);
	const roundsUp = Number(fraction[WAGE_VALUE_DECIMALS]) >= 5;
	return (truncatedCents + (roundsUp ? 1 : 0)) / 100;
};

/**
 * `update_dept_config` no longer writes the training/orientation columns, so the Settings form's
 * page-load copy must not be echoed into `chosenDepartment` — the Team tab reads its baseline there.
 */
export const withPreservedTrainingOrientationWage = (incoming: DepartmentWageConfig, previous: DepartmentWageConfig | undefined): DepartmentWageConfig => {
	if (!previous) return incoming;
	return {
		...incoming,
		orientationWageType: previous.orientationWageType,
		orientationWageValue: previous.orientationWageValue,
		trainingWageType: previous.trainingWageType,
		trainingWageValue: previous.trainingWageValue,
	};
};

/** The three wage rule tables DAT-905 moved to Team → Wage Settings. */
type WageRulesConfig = Pick<Department, "hoursRateConfig" | "overtimeConfig" | "breakRulesConfig">;

/**
 * DAT-905 · Same reason as `withPreservedTrainingOrientationWage`, for the hours-rate, overtime and
 * break-rules tables: `update_dept_config` no longer writes them, so the Settings save's echo must
 * keep the copies already in `chosenDepartment`. The Team tab reads its baseline there, and a
 * Settings save must not revert an edit it made.
 */
export const withPreservedWageRulesConfig = <T extends WageRulesConfig>(incoming: T, previous: WageRulesConfig): T => ({
	...incoming,
	hoursRateConfig: previous.hoursRateConfig,
	overtimeConfig: previous.overtimeConfig,
	breakRulesConfig: previous.breakRulesConfig,
});

/** The pair as stored. The success path writes these back into context instead of refetching. */
export const quantizeTrainingOrientationWage = (body: TrainingOrientationWageBody): TrainingOrientationWageBody => ({
	...body,
	orientationWageValue: quantizeWageValue(body.orientationWageValue),
	trainingWageValue: quantizeWageValue(body.trainingWageValue),
});

export const getDepartmentWageConfig = (department?: Department): DepartmentWageConfig => {
	const defaults: DepartmentWageConfig = {
		overtimeRatePayPercentage: DEFAULT_OVERTIME_RATE_PAY_PERCENTAGE,
		nightRatePayValue: DEFAULT_NIGHT_PAY_VALUE,
		weekendRatePayValue: DEFAULT_WEEKEND_PAY_VALUE,
		holidayRatePayPercentage: DEFAULT_HOLIDAY_RATE_PAY_PERCENTAGE,
		trainingWageType: DEFAULT_TRAINING_WAGE_TYPE,
		trainingWageValue: DEFAULT_TRAINING_WAGE_AMOUNT,
		orientationWageType: DEFAULT_ORIENTATION_WAGE_TYPE,
		orientationWageValue: DEFAULT_ORIENTATION_WAGE_AMOUNT,
		overtimeDailyHoursLimit: DEFAULT_OVERTIME_DAILY_HOURS_LIMIT,
		overtimeWeeklyHoursLimit: DEFAULT_OVERTIME_WEEKLY_HOURS_LIMIT,
	};

	const config = department?.department_wage_config as DepartmentWageConfig | undefined;
	if (!config) {
		return defaults;
	}

	return {
		overtimeRatePayPercentage: config.overtimeRatePayPercentage ?? defaults.overtimeRatePayPercentage,
		nightRatePayValue: { ...defaults.nightRatePayValue, ...(config.nightRatePayValue ?? {}) },
		weekendRatePayValue: { ...defaults.weekendRatePayValue, ...(config.weekendRatePayValue ?? {}) },
		holidayRatePayPercentage: config.holidayRatePayPercentage ?? defaults.holidayRatePayPercentage,
		trainingWageType: config.trainingWageType ?? defaults.trainingWageType,
		trainingWageValue: config.trainingWageValue ?? defaults.trainingWageValue,
		orientationWageType: config.orientationWageType ?? defaults.orientationWageType,
		orientationWageValue: config.orientationWageValue ?? defaults.orientationWageValue,
		overtimeDailyHoursLimit: config.overtimeDailyHoursLimit ?? defaults.overtimeDailyHoursLimit,
		overtimeWeeklyHoursLimit: config.overtimeWeeklyHoursLimit ?? defaults.overtimeWeeklyHoursLimit,
	};
};
